import React from 'react'
import FdDialog from '@/Components/FdComponents/FdDialog'
import { Typography, Box } from '@mui/material'

type UseConfirmationDialogReturnType = {
  isVisible: boolean
  setVisibility: (value: boolean) => void
  onClose: () => void
}

type ConfirmationDialogArguments = {
  confirmationTitle: string
  confirmationMessage: string
  confirmationButtonLabel: string
  cancelButtonLabel: string
}

type ConfirmationDialogContextType = {
  showConfirmation: (confirmationDialogArguments: ConfirmationDialogArguments) => Promise<boolean>
}

type ConfirmationDialogContextProviderProps = {
  children: React.ReactNode
}

const useConfirmationDialog = (): UseConfirmationDialogReturnType => {
  const [isVisible, setVisibility] = React.useState<boolean>(false)

  const handleOnClose = () => setVisibility(false)

  return {
    isVisible,
    setVisibility,
    onClose: handleOnClose
  }
}

const ConfirmationDialogContext = React.createContext<ConfirmationDialogContextType>({} as ConfirmationDialogContextType)

const ConfirmationDialogContextProvider: React.FC<ConfirmationDialogContextProviderProps> = (props) => {
  const { setVisibility, isVisible, onClose } = useConfirmationDialog()
  const [content, setContent] = React.useState<ConfirmationDialogArguments>({
    confirmationTitle: '',
    confirmationMessage: '',
    confirmationButtonLabel: 'Yes, delete',
    cancelButtonLabel: 'No'
  })
  const resolver = React.useRef<Function>()

  const handleShow = (confirmationDialogArguments: ConfirmationDialogArguments): Promise<boolean> => {
    const { confirmationTitle, confirmationMessage, confirmationButtonLabel, cancelButtonLabel } = confirmationDialogArguments

    setContent({
      confirmationTitle,
      confirmationMessage,
      confirmationButtonLabel,
      cancelButtonLabel
    })
    setVisibility(true)

    return new Promise(function (resolve) {
      resolver.current = resolve
    })
  }

  const [modalContext] = React.useState<ConfirmationDialogContextType>({
    showConfirmation: handleShow
  })

  const handleConfirm = () => {
    resolver.current && resolver.current(true)
    onClose()
  }

  const handleCancel = () => {
    resolver.current && resolver.current(false)
    onClose()
  }

  const handleOnClose = () => {
    resolver.current && resolver.current(false)
    onClose()
  }

  return (
    <ConfirmationDialogContext.Provider value={modalContext}>
      {props.children}
      {content.confirmationTitle && content.confirmationMessage && (
        <FdDialog
          open={isVisible}
          onClose={handleOnClose}
          title={content?.confirmationTitle}
          maxWidth='xs'
          fullWidth
          customSx={{
            '& .MuiDialogActions-root': {
              '& .MuiButton-root': {
                width: '100%'
              }
            }
          }}
          content={
            <Box sx={{ pt: 2 }}>
              <Typography>{content?.confirmationMessage}</Typography>
            </Box>
          }
          confirmLabel={content.confirmationButtonLabel}
          onConfirm={handleConfirm}
          cancelLabel={content.cancelButtonLabel}
          onCancel={handleCancel}
        />
      )}
    </ConfirmationDialogContext.Provider>
  )
}

const useConfirmationDialogContext = (): ConfirmationDialogContextType => React.useContext(ConfirmationDialogContext)

export { useConfirmationDialog, useConfirmationDialogContext }

export default ConfirmationDialogContextProvider
